import { Component, type ErrorInfo, type ReactNode } from "react";

import logo from "$/assets/logo.png";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-[#FFFCFA] p-4">
          <div className="w-full max-w-md">
            <div className="rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 p-8 shadow-xl">
              {/* Logo */}
              <div className="mb-6 flex justify-center">
                <img src={logo} alt="Lybe Logo" className="h-16 w-auto" />
              </div>

              {/* Title */}
              <h1 className="mb-1 text-center text-[27px] font-bold text-gray-900">
                Oups !
              </h1>
              <h2 className="mb-4 text-center text-2xl font-bold text-gray-900">
                Une erreur est survenue
              </h2>

              {/* Error Details (only in development) */}
              {import.meta.env.DEV && this.state.error && (
                <details className="mb-6 rounded-lg bg-gray-50 p-4">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700">
                    Détails techniques
                  </summary>
                  <div className="mt-2 space-y-2">
                    <p className="font-mono text-xs text-red-600">
                      {this.state.error.toString()}
                    </p>
                    {this.state.errorInfo && (
                      <pre className="max-h-40 overflow-auto text-xs text-gray-600">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={this.handleReset}
                  className="w-full rounded-lg bg-primary px-4 py-3 font-medium text-white transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  Retourner à l'accueil
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                >
                  Recharger la page
                </button>
              </div>

              {/* Support Link */}
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  Besoin d'aide ?{" "}
                  <a
                    href="mailto:contact@libe.app"
                    className="font-medium text-primary hover:underline"
                  >
                    Contactez notre support
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
